import httpClient from '@/services/httpClient'
import {
  IAttendanceIsOpenned,
  IProfessionalData,
  IDisease,
  IAttachmentPayload,
  ISymptom,
  IScreening,
  IStartScreeningPayload,
  IUncharacterizedPersonalData,
  IMedicine,
  IMedication,
  IAllergy,
  IAllergyResponse,
  IGetScreeningResponse,
  IPatientProfileData,
} from './types'
import filterScreeningPayloadFromStep from './utils/filterScreeningPayloadFromStep'
import { allergyTranslatePtToEng } from './utils/allergyResponseParser'
import { AxiosResponse } from 'axios'
import { attachmentTypeTranslatePtToEng } from '@/modules/Attachment/utils/attachmentTypeTranslation'
import { formatToBRDate } from '@/utils/date'

export default {
  async getScreeningStepsByProfessionalType(
    professionalType: string,
  ): Promise<string[]> {
    return httpClient
      .get(`/integration/app-paciente/triagem/etapas/${professionalType}`)
      .then(res => res.data.object)
  },

  async startScreening(professionalType: string): Promise<IScreening> {
    return httpClient
      .post(`/integration/app-paciente/triagem/${professionalType}`)
      .then((res: IStartScreeningPayload) => {
        return {
          token: res.data.object.id,
          patient: {
            id: res.data.object.idPaciente,
            name: '',
          },
          clinic: {
            id: res.data.object.idClinica,
            hasFreeAttendances: 'hasNoFreeAttendanceAllowed',
          },
          professional: {
            id: res.data.object.tipoProfissional.id,
            description: res.data.object.tipoProfissional.descricao,
            council: res.data.object.tipoProfissional.tipoConselho,
          },
          startedAt: res.data.object.dataInicio,
          finishedAt: res.data.object.dataFim,
        }
      })
  },

  async updateScreening(
    token: string,
    step: string,
    data: IScreening,
  ): Promise<void> {
    return httpClient.put(`/integration/app-paciente/triagem/${token}`, {
      etapaTriagem: step,
      ...filterScreeningPayloadFromStep(step, data),
    })
  },

  async setAttendanceIsOpenned(
    token: string,
    payload: IAttendanceIsOpenned,
  ): Promise<void> {
    return httpClient.post(
      `/integration/app-paciente/triagem/${token}/chamado`,
      payload,
    )
  },

  async getScreeningById(token: string): Promise<IScreening> {
    return httpClient
      .get(`/integration/app-paciente/triagem/${token}`)
      .then((res: IGetScreeningResponse) => ({
        token: res.data.object.id,
        attachments: res.data.object?.anexos?.map(attachment => ({
          id: attachment.idAnexo,
          description: attachment.legenda,
          size: parseInt(attachment.tamanho, 10),
          extension: attachment.nomeExtensao,
          attachmentType: attachmentTypeTranslatePtToEng(attachment.tipoAnexo),
          date: new Date(attachment.dataExame),
          status: 'UPLOADED',
        })),
        nps:
          res.data.object.npsTriagem === null
            ? null
            : {
                rating: res.data.object.npsTriagem.nota,
                suggestion: res.data.object.npsTriagem.comentario,
              },
      }))
  },

  async getSymptonsByProfessionalType(
    professionalType: string,
  ): Promise<ISymptom[]> {
    return httpClient
      .get(`/integration/app-paciente/sintomas/${professionalType}`)
      .then(res =>
        res.data.object?.map(data => ({
          id: data.id,
          description: data.descricao,
          image: data.imagem,
        })),
      )
  },

  async getTopSymptonsByProfessionalType(
    professionalType: string,
  ): Promise<ISymptom[]> {
    return httpClient
      .get(`/integration/app-paciente/sintomas/top/${professionalType}`)
      .then(res =>
        res.data.object?.map(data => ({
          id: data.id,
          description: data.descricao,
          image: data.imagem,
        })),
      )
  },

  async getTopSymptonsByProfessionalAndClinic(
    professionalType: string,
  ): Promise<ISymptom[]> {
    return httpClient
      .get(`/integration/app-paciente/sintomas/top/${professionalType}`)
      .then(res =>
        res.data.object?.map(data => ({
          id: data.id,
          description: data.descricao,
          image: data.imagem,
        })),
      )
  },

  async searchSymptons(payload: string): Promise<IDisease[]> {
    return httpClient
      .get(`integration/app-paciente/sintomas/autocomplete/${payload}`)
      .then(res =>
        res.data?.object?.map(d => ({ id: d?.id, description: d?.descricao })),
      )
  },

  async getFocusedSymptonsByProfessionalType(
    professionalType: string,
  ): Promise<void> {
    return httpClient
      .get(`/integration/app-paciente/sintomas/destacados/${professionalType}`)
      .then(res => res.data.object)
  },

  async getUncharacterizedPersonalData(): Promise<IUncharacterizedPersonalData> {
    return httpClient
      .get(`/integration/app-paciente/dados-pessoais/descaracterizados`)
      .then(res => ({
        name: res.data.object.name,
        birthday: res.data.object.dataNascimento,
        cpf: res.data.object.cpf,
        cellphone: res.data.object.celular,
        address: res.data.object.endereco,
        email: res.data.object.email,
        complement: res.data.object.complemento,
        city: res.data.object.cidade,
        postalCode: res.data.object.cep,
        neighborhood: res.data.object.bairro,
        number: res.data.object.numeroLogradouro,
        uf: res.data.object.uf,
      }))
  },

  async savePersonalData(payload: IProfessionalData): Promise<void> {
    return httpClient
      .post(`/integration/app-paciente/dados-pessoais`, payload)
      .then(res => res.data.object)
  },

  async getScreeningDiseases(): Promise<IDisease[]> {
    return httpClient
      .get(`/integration/app-paciente/doencas-triagem`)
      .then(res => {
        return res.data.object?.map(disease => ({
          id: disease.id,
          description: disease.descricao,
        }))
      })
  },

  async getPatientDiseases(): Promise<IDisease[]> {
    return httpClient
      .get(`/integration/app-paciente/doencas`)
      .then(res => res?.data?.object?.map(d => d?.doencaTriagem))
      .then(res => res?.map(d => ({ id: d?.id, description: d?.descricao })))
  },

  async searchDiseases(payload: string): Promise<IDisease[]> {
    return httpClient
      .get(`integration/app-paciente/doencas-triagem/autocomplete/${payload}`)
      .then(res =>
        res?.data?.object?.map(d => ({ id: d?.id, description: d?.descricao })),
      )
  },

  async getPatientMedicines(): Promise<IMedicine[]> {
    return httpClient
      .get(`/integration/app-paciente/medicamentos`)
      .then(res => res?.data?.object)
      .then(res =>
        res?.map(m => ({
          id: m?.medicamento?.id,
          description: m?.medicamento?.descricao,
          medicine: m?.medicamento?.nome,
          quantity: m?.dose,
        })),
      )
  },

  async getPatientMedicineHistory(id: number): Promise<void> {
    return httpClient
      .get(`/integration/app-paciente/medicamentos/paciente/${id}/historico`)
      .then(res => res.data.object)
  },

  async getAllergies(): Promise<IAllergy> {
    return httpClient
      .get(`/integration/app-paciente/alergias`)
      .then(({ data }: AxiosResponse<IAllergyResponse>) => ({
        isAllergicToIodine: allergyTranslatePtToEng(
          data.object.temAlergiaContrasteIodo,
        ),
        isAllergicToSeafood: allergyTranslatePtToEng(
          data.object.temAlergiaFrutosDoMar,
        ),
        isAllergicToMedicines: allergyTranslatePtToEng(
          data.object.temAlergiaMedicamentos,
        ),
        medicines: data?.object?.alergiasMedicamentos?.map(medicine => ({
          description: medicine.alergia,
          medicine: medicine.alergia,
          id: medicine.codigoPrincipioAtivo,
        })),
      }))
  },

  async savePatientAllergies(payload: IAllergyResponse): Promise<void> {
    return httpClient.post(`/integration/app-paciente/alergias`, payload)
  },

  async validateAttachment(payload: IAttachmentPayload): Promise<void> {
    return httpClient
      .post(`/integration/app-paciente/triagem/validar-anexo`, payload)
      .then(res => res.data.object)
  },

  async validateReschedule(idAtendimento: number): Promise<void> {
    return httpClient
      .get(
        `/integration/app-paciente/atendimento/${idAtendimento}/verificar-reagendamento`,
      )
      .then(res => res.data.object)
  },

  async getMedicationsByTerm(term: string): Promise<IMedication[]> {
    return httpClient
      .get(`/integration/app-paciente/util/medicamento/buscar/${term}`)
      .then(res => res.data.object)
  },

  async updateProfileInformation(
    data: IPatientProfileData,
    emergencyContactId?: number,
  ): Promise<boolean> {
    const requestData = {
      celular: data.cellphone,
      dataNascimento: data?.birthdate,
      email: data.email,
      cep: data.cep,
      sexo: data.sex,
      endereco: data.streetName,
      complemento: data.complement,
      cidade: data.city,
      numeroLogradouro: data.number,
      bairro: data.neighborhood,
      uf: data.state,
      carteiraConvenio: data.healthInsuranceNumber,
      contatoEmergencia: {
        nomeCompleto: data.emergencyContactFullName,
        telefone: data.emergencyContactCellphone,
        idContatoEmergencia: emergencyContactId,
        relacao: data.relationship,
      },
      informacaoGestante: {
        semanas: data?.weeksPregnancy || '',
        dias: data?.daysPregnancy || '',
        dataUltimaMenstruacao:
          formatToBRDate(data.lastMenstrualPeriodDate) || '',
      },
    }
    const response = await httpClient.put(
      '/integration/app-paciente/dados-pessoais/alterar-perfil/triagem',
      requestData,
    )

    return response?.data?.object
  },
  async getEmergencyContactId(): Promise<number> {
    return httpClient
      .get(`integration/app-paciente/dados-pessoais/contato-emergencia`)
      .then(res => res?.data?.object?.idContatoEmergencia)
  },
  async updateUserDataInformation(
    data: IPatientProfileData,
    emergencyContactId?: number,
  ): Promise<boolean> {
    const requestData = {
      telefoneCelular: data.cellphone,
      email: data.email,
      endereco: data.streetName,
      numeroLogradouro: data.number,
      complemento: data.complement,
      bairro: data.neighborhood,
      uf: data.state,
      carteiraConvenio: data.healthInsuranceNumber,
      sexo: data.sex,
      dataNascimento: data.birthdate,
      cep: data.cep,
      cidade: data.city,
      contatoEmergencia: {
        telefone: data.emergencyContactCellphone,
        nomeCompleto: data.emergencyContactFullName,
        idContatoEmergencia: emergencyContactId,
        relacao: data.relationship,
      },
      informacaoGestante: {
        semanas: data?.weeksPregnancy || '',
        dias: data?.daysPregnancy || '',
        dataUltimaMenstruacao:
          formatToBRDate(data.lastMenstrualPeriodDate) || '',
      },
    }

    const response = await httpClient.post(
      '/integration/app-paciente/dados-pessoais/alterar-dados',
      requestData,
    )

    return response?.data?.object
  },
}
